import { useParams, useRouterState } from '@tanstack/react-router'
import { useChatbotAttachmentsOptional } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { useChatbotReferenceActionsOptional } from 'features/chatbot/contexts/ChatbotReferenceActionsContext'
import { useOpenTrendResource } from 'features/facilityModel/useFacilityModelNavigation'
import { useCallback } from 'react'

const PROCESS_DATA_LABEL = 'Process Data'

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Opens instrument references from the chatbot: page-provided actions first
 * (Model Operations drives its own controlled chart), otherwise Process Data.
 */
export function useInstrumentReferenceFallback(): (
  entryIds: string[],
  timeWindow?: { from: string; to: string }
) => void {
  const referenceActions = useChatbotReferenceActionsOptional()
  const chatbotAttachments = useChatbotAttachmentsOptional()
  const params = useParams({ strict: false })
  const search = useRouterState({
    select: (state) => state.location.search,
  }) as Record<string, unknown>

  const siteId =
    getStringValue(params.siteID) ??
    getStringValue(search.siteId) ??
    getStringValue(search.siteID)
  const openTrend = useOpenTrendResource(siteId, 'chatbot')

  return useCallback(
    (entryIds, timeWindow) => {
      if (entryIds.length === 0) return

      const effectiveWindow =
        timeWindow ??
        chatbotAttachments?.attachments.find((a) => a.type === 'timeWindow')
          ?.timeWindow

      if (referenceActions) {
        referenceActions.addInstruments(entryIds, effectiveWindow)
        return
      }

      openTrend(entryIds, PROCESS_DATA_LABEL, effectiveWindow)
    },
    [referenceActions, chatbotAttachments, openTrend]
  )
}
